"use client"

import React, { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"

import { Clock, CheckCircle2, Play, Activity, FileText, MessageSquare, Square, RotateCcw, Info, Loader2, AlertTriangle } from 'lucide-react'
import { useAppContext } from "../../../../shared/functions/Context"
import { useNavigate } from "react-router-dom"
import { ITemplateRating } from "../../../../shared/models/three-sixty-feedback-models/TemplateRating"
import { defaultTemplate, ITemplates } from "../../../../shared/models/three-sixty-feedback-models/Templates"
import MODAL_NAMES from "../../../dialogs/ModalName"
import showModalFromId from "../../../../shared/functions/ModalShow"
import Modal from "../../../../shared/components/Modal"
import SavedAlertModal from "../../shared-components/SavedAlertModal"

// Helper functions for consistent styling (copied from AdminQuestionnaireBox)
const getRatingColor = (index: number) => {
  const colors = [
    "from-red-500 to-red-600", // Very Bad
    "from-orange-500 to-orange-600", // Bad
    "from-yellow-500 to-yellow-600", // Better
    "from-blue-500 to-blue-600", // Good
    "from-green-500 to-green-600", // Excellent
  ]
  return colors[index]
}

const getRatingLabel = (index: number) => {
  const labels = ["Very Bad", "Bad", "Better", "Good", "Excellent"]
  return labels[index]
}

const TemplateQuestionnaireBox: React.FC = observer(() => {
  const { api, store } = useAppContext()
  const navigate = useNavigate()
  const [selectedSessionId, setSelectedSessionId] = useState<string>("")
  const [session, setSession] = useState<ITemplateRating | null>(null)
  const [startValue, setStartValue] = useState("")
  const [stopValue, setStopValue] = useState("")
  const [template, setTemplate] = useState<ITemplates>(defaultTemplate)
  const [showNotification, setShowNotification] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [additionalComment, setAdditionalComment] = useState("")
  const [continueValue, setContinueValue] = useState("")
  const me = store.auth.meJson?.uid
  const allSessions = store.templateRating.all.map((session) => session.asJson)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        await api.templateRating.getAll()
        await api.templates.getAll()
        await api.user.getAll()
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [api])

  useEffect(() => {
    if (store.templateRating.selected) {
      const selectedSession = store.templateRating.selected
      setSession(selectedSession)
      if (selectedSession) {
        handleSessionClick(selectedSession.id)
      }
    }
  }, [store.templateRating.selected])

  const updateCheckedMapFromSession = (selectedSession: ITemplateRating | null) => {
    if (selectedSession) {
      const newCheckedMap: Record<string, Record<number, boolean>> = {}
      const raterId = me ?? ""
      const ratings = selectedSession.values[raterId]?.ratings
      if (ratings) {
        Object.entries(ratings[template.title] || {}).forEach(
          ([statement, score]) => {
            const statementIndex = template.statements.findIndex(
              (s) => s.statement === statement,
            )
            if (statementIndex !== -1) {
              const ratingIndex = 5 - score // Assuming score 1-5 maps to index 4-0
              newCheckedMap[statementIndex] = { [ratingIndex]: true }
            }
          },
        )
      }
      // setIsCheckedMap(newCheckedMap); // This state is not used in the current logic, but kept for reference
    }
  }

  const handleSessionClick = async (sessionId: string) => {
    const selectedSession = allSessions.find((session) => session.id === sessionId)
    if (selectedSession) {
      const templateId = selectedSession.templateId
      const allTemplates = store.templates.all.map((temp) => temp.asJson)
      const foundTemplate = allTemplates.find((t) => t.id === templateId)
      setTemplate(foundTemplate || defaultTemplate)
      setSession(selectedSession)
      if (selectedSession.values[me ?? ""]) {
        setStartValue(selectedSession.values[me ?? ""].startDoing || "")
        setStopValue(selectedSession.values[me ?? ""].stopDoing || "")
        setContinueValue(selectedSession.values[me ?? ""].continueDoing || "")
        setAdditionalComment(
          selectedSession.values[me ?? ""].additionalComment || "",
        )
      } else {
        setStartValue("")
        setStopValue("")
        setContinueValue("")
        setAdditionalComment("")
      }
      updateCheckedMapFromSession(selectedSession)
    } else {
      setTemplate(defaultTemplate)
      setStartValue("")
      setStopValue("")
      setContinueValue("")
      setAdditionalComment("")
    }
    setSelectedSessionId(sessionId)
  }

  const handleCheckboxChange = (
    statementIndex: number,
    ratingIndex: number,
    isChecked: boolean,
  ) => {
    // This logic needs to be updated to correctly manage the selected rating for each statement
    // The original code had a bug where it would only store one rating per statement.
    // It should ensure only one checkbox is checked per statement.

    if (session) {
      const updatedSession = { ...session }
      const raterId = me ?? ""
      const ratingValue = ratingIndex + 1
      const statementText = template.statements[statementIndex].statement

      if (!updatedSession.values[raterId]) {
        updatedSession.values[raterId] = {
          additionalComment: "",
          stopDoing: "",
          startDoing: "",
          continueDoing: "",
          ratings: {},
        }
      }
      if (!updatedSession.values[raterId].ratings[template.title]) {
        updatedSession.values[raterId].ratings[template.title] = {}
      }

      if (isChecked) {
        // Set the new rating
        updatedSession.values[raterId].ratings[template.title][statementText] = ratingValue
      } else {
        // If unchecked, remove the rating for this statement
        delete updatedSession.values[raterId].ratings[template.title][statementText]
      }
      setSession(updatedSession)
    }
  }

  const handleSaveRatings = async () => {
    setShowNotification(false) // Reset notification

    // Validate required text areas for Start, Stop, and Continue inputs
    if (!startValue || !stopValue || !continueValue) {
      setShowNotification(true)
      return
    }

    // Validate ratings for each statement
    let allStatementsRated = true
    if (session && template) {
      const raterId = me ?? ""
      const currentRatings = session.values[raterId]?.ratings[template.title] || {}

      template.statements.forEach((statement) => {
        const statementText = statement.statement
        if (!(statementText in currentRatings) || currentRatings[statementText] === undefined) {
          allStatementsRated = false
        }
      })

      if (!allStatementsRated) {
        setShowNotification(true)
        return
      }
    } else {
      console.log("Session or Template is missing.")
      return
    }

    // All validations passed, update the session with the ratings
    if (session) {
      const updatedSession: ITemplateRating = { ...session }
      const raterId = me ?? ""

      if (!updatedSession.values[raterId]) {
        updatedSession.values[raterId] = {
          additionalComment: "",
          stopDoing: "",
          startDoing: "",
          continueDoing: "",
          ratings: {},
        }
      }

      updatedSession.values[raterId] = {
        ...updatedSession.values[raterId],
        startDoing: startValue,
        stopDoing: stopValue,
        continueDoing: continueValue,
        additionalComment: additionalComment,
      }

      console.log("Updated session: ", updatedSession)
      setSession(updatedSession) // Update local state immediately

      try {
        await api.templateRating.update({
          ...updatedSession,
          status: "Completed",
        })
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.SUCCESSFULLY_SUBMITTED_MODAL)
        navigate(`/c/threesixty/home/overview?tab=surveys-requests`)
      } catch (error) {
        console.error("Failed to save ratings:", error)
        // Optionally show an error snackbar
      }
    }
  }

  const onBack = async () => {
    if (session && session.status === "Completed") {
      navigate(`/c/threesixty/home/overview?tab=surveys-requests`)
    } else if (session) {
      const updatedSession = { ...session }
      const raterId = me ?? ""

      if (!updatedSession.values[raterId]) {
        updatedSession.values[raterId] = {
          additionalComment: "",
          stopDoing: "",
          startDoing: "",
          continueDoing: "",
          ratings: {},
        }
      }

      updatedSession.values[raterId] = {
        ...updatedSession.values[raterId],
        startDoing: startValue,
        stopDoing: stopValue,
        continueDoing: continueValue,
        additionalComment: additionalComment,
      }

      console.log("Updated session on back:", updatedSession)
      setSession(updatedSession) // Update local state immediately

      try {
        await api.templateRating.update({
          ...updatedSession, // Use updatedSession here
          status: "In Progress",
        })
        navigate(`/c/threesixty/home/overview?tab=surveys-requests`)
      } catch (error) {
        console.error("Failed to save progress on back:", error)
        // Optionally show an error snackbar
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {session && template && (
        <>
          <div
            className={`bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200 shadow-lg overflow-hidden`}
          >
            {/* Item Header */}
            <div className="bg-white/60 backdrop-blur-sm p-6 border-b border-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <FileText className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">{template.title}</h3>
                  <p className="text-sm text-slate-600">Rate the statements below</p>
                </div>
              </div>
            </div>
            {/* Statements */}
            <div className="p-6 space-y-6">
              {template.statements.map((statement, statementIndex) => {
                const raterId = me ?? ""
                const currentRating =
                  session.values[raterId]?.ratings[template.title]?.[statement.statement] || 0
                return (
                  <div key={statementIndex} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm">
                    <div className="mb-6">
                      <p className="text-lg font-medium text-slate-800 leading-relaxed">
                        {statement.statement}
                        <span className="text-red-500 ml-1">*</span>
                      </p>
                    </div>
                    {/* Rating Options */}
                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                      {Array.from({ length: 5 }).map((_, ratingIndex) => {
                        const isChecked = currentRating === ratingIndex + 1
                        return (
                          <label
                            key={ratingIndex}
                            className={`relative group transition-all duration-300 ${
                              session.status === "Completed" ? "cursor-not-allowed" : "cursor-pointer"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only" // Visually hide the checkbox
                              onChange={(e) =>
                                handleCheckboxChange(
                                  statementIndex,
                                  ratingIndex,
                                  e.target.checked,
                                )
                              }
                              checked={isChecked}
                              value={`${ratingIndex + 1}`}
                              required
                              disabled={session.status === "Completed"}
                            />
                            <div
                              className={`relative p-4 rounded-xl border-2 transition-all duration-300 ${
                                isChecked
                                  ? `bg-gradient-to-br ${getRatingColor(ratingIndex)} border-transparent text-white shadow-lg`
                                  : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-md"
                              }`}
                            >
                              {/* Rating Number */}
                              <div className="text-center mb-2">
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mx-auto ${
                                    isChecked ? "bg-white/20" : "bg-slate-100"
                                  }`}
                                >
                                  {ratingIndex + 1}
                                </div>
                              </div>
                              {/* Rating Label */}
                              <div className="text-center">
                                <span className={`text-sm font-semibold ${isChecked ? "text-white" : "text-slate-700"}`}>
                                  {getRatingLabel(ratingIndex)}
                                </span>
                              </div>
                              {/* Check Icon */}
                              {isChecked && (
                                <div className="absolute top-2 right-2">
                                  <CheckCircle2 className="h-5 w-5 text-white" />
                                </div>
                              )}
                              {/* Hover Effect */}
                              <div
                                className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
                                  isChecked
                                    ? "opacity-0"
                                    : "opacity-0 group-hover:opacity-10 bg-gradient-to-br " + getRatingColor(ratingIndex)
                                }`}
                              />
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Feedback Section - Start, Stop, Continue, Additional Comments */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Feedback Comments</h3>
                <p className="text-sm text-slate-600">Provide specific feedback for improvement</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Start Doing */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Play className="h-4 w-4 text-green-600" />
                  <label htmlFor="start-doing" className="text-sm font-semibold text-slate-800">
                    Start Doing <span className="text-red-500">*</span>
                  </label>
                  <div className="group relative">
                    <Info className="h-4 w-4 text-slate-400 cursor-help" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      What behaviors should they start doing to improve performance?
                    </div>
                  </div>
                </div>
                <textarea
                  id="start-doing"
                  className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition-all duration-200"
                  value={startValue}
                  maxLength={400}
                  rows={4}
                  placeholder="What should they start doing to improve performance?"
                  onChange={(e) => setStartValue(e.target.value)}
                  required
                  disabled={session.status === "Completed"}
                ></textarea>
                <div className="text-xs text-slate-500 text-right">{startValue.length}/400</div>
              </div>
              {/* Stop Doing */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Square className="h-4 w-4 text-red-600" />
                  <label htmlFor="stop-doing" className="text-sm font-semibold text-slate-800">
                    Stop Doing <span className="text-red-500">*</span>
                  </label>
                  <div className="group relative">
                    <Info className="h-4 w-4 text-slate-400 cursor-help" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      What behaviors should they stop doing as they hinder their performance or the team's effectiveness?
                    </div>
                  </div>
                </div>
                <textarea
                  id="stop-doing"
                  className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition-all duration-200"
                  value={stopValue}
                  maxLength={400}
                  rows={4}
                  placeholder="What should they stop doing that hinders performance?"
                  onChange={(e) => setStopValue(e.target.value)}
                  required
                  disabled={session.status === "Completed"}
                ></textarea>
                <div className="text-xs text-slate-500 text-right">{stopValue.length}/400</div>
              </div>
              {/* Continue Doing */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 text-blue-600" />
                  <label htmlFor="continue-doing" className="text-sm font-semibold text-slate-800">
                    Continue Doing <span className="text-red-500">*</span>
                  </label>
                  <div className="group relative">
                    <Info className="h-4 w-4 text-slate-400 cursor-help" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      What behaviors should they continue doing that contributes positively?
                    </div>
                  </div>
                </div>
                <textarea
                  id="continue-doing"
                  className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200"
                  value={continueValue}
                  maxLength={400}
                  rows={4}
                  placeholder="What should they continue doing that contributes positively?"
                  onChange={(e) => setContinueValue(e.target.value)}
                  required
                  disabled={session.status === "Completed"}
                ></textarea>
                <div className="text-xs text-slate-500 text-right">{continueValue.length}/400</div>
              </div>
            </div>
            {/* Additional Comments */}
            <div className="mt-6 space-y-3">
              <label htmlFor="additional-comments" className="text-sm font-semibold text-slate-800">
                Additional Comments (Optional)
              </label>
              <textarea
                id="additional-comments"
                className="w-full p-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200"
                value={additionalComment}
                maxLength={500}
                rows={4}
                placeholder="Any additional feedback or context you'd like to provide..."
                onChange={(e) => setAdditionalComment(e.target.value)}
                disabled={session.status === "Completed"}
              ></textarea>
              <div className="text-xs text-slate-500 text-right">{additionalComment.length}/500</div>
            </div>
          </div>

          {showNotification && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 animate-fadeIn">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-red-800 text-sm">
                Please fill in all required fields and select exactly one rating for each statement.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 p-6 border-t border-slate-200/50 bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg">
            <button
              onClick={onBack}
              className="px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              disabled={loading}
            >
              Back
            </button>
            <button
              onClick={handleSaveRatings}
              className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              disabled={session.status === "Completed" || loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Submit Ratings
                </>
              )}
            </button>
          </div>
        </>
      )}
      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.SUCCESSFULLY_SUBMITTED_MODAL}>
        <SavedAlertModal />
      </Modal>
    </div>
  )
})

export default TemplateQuestionnaireBox
